import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { getInsight, insights } from "../data/insights";
import { useLang } from "../i18n/LanguageProvider";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
}

export default function InsightDetail() {
  const { slug } = useParams();
  const { t } = useLang();
  const article = getInsight(slug);
  if (!article) return <Navigate to="/insights" replace />;

  const next =
    insights[(insights.findIndex((x) => x.slug === article.slug) + 1) % insights.length];

  return (
    <>
      <article className="pt-40 lg:pt-48 pb-24">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-gold text-[11px] tracking-[0.28em] uppercase mb-10 hover:gap-3 transition-all"
            data-testid="insight-back"
          >
            <ArrowLeft size={14} /> {t("insights.back")}
          </Link>

          <div className="eyebrow mb-6">{article.category}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-10">
            {article.title}
          </h1>
          <p className="text-muted text-lg leading-relaxed max-w-2xl">{article.dek}</p>

          <div className="mt-10 pt-6 border-t border-[var(--bw-border)] flex items-center justify-between text-sm text-muted">
            <div>
              <span className="text-white/85">{article.author}</span>
              <span className="mx-3">·</span>
              <span>{formatDate(article.date)}</span>
            </div>
            <div>{article.reading}</div>
          </div>

          <div className="mt-16 space-y-14" data-testid="insight-body">
            {article.sections.map((s, i) => (
              <section key={s.heading} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-3">
                  <div className="font-serif-display text-gold text-2xl">0{i + 1}</div>
                </div>
                <div className="lg:col-span-9">
                  <h2 className="font-serif-display text-2xl lg:text-3xl text-white mb-5 leading-tight">
                    {s.heading}
                  </h2>
                  <p className="text-white/85 leading-relaxed text-lg">{s.body}</p>
                </div>
              </section>
            ))}
          </div>
        </div>
      </article>

      {/* NEXT */}
      <section className="border-t border-[var(--bw-border)] py-20 bg-surface">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="text-[10px] tracking-[0.32em] uppercase text-gold mb-4">Continue reading</div>
          <Link
            to={`/insights/${next.slug}`}
            className="group flex items-start justify-between gap-6"
          >
            <div>
              <div className="text-muted text-sm mb-2">{next.category}</div>
              <h3 className="font-serif-display text-2xl lg:text-3xl text-white group-hover:text-gold transition-colors leading-tight">
                {next.title}
              </h3>
              <p className="text-muted mt-3 max-w-lg">{next.dek}</p>
            </div>
            <ArrowUpRight
              size={22}
              className="text-gold shrink-0 mt-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </Link>
        </div>
      </section>
    </>
  );
}
